import { useState } from 'react';
import cn from 'classnames';
import { fromWei } from '@/services/blockchain';
import ReactPlayer from 'react-player';
import { Button, Flex, Popover } from '@/lib';
import styles from './NFTCard.module.scss';
import { SharePopupContent } from '../SharePopupContent';
import { Countdown } from '../Countdown';
import { NFTCardSettingsPopupContent } from './NFTSettingsPopupContent';
import { useWeb3ModalAccount } from '@web3modal/ethers5/react';
import { truncate } from '@/utils/truncate';
import { PutOnSaleModal } from '../PutOnSaleModal';
import { RemoveFromSaleModal } from '../RemoveFromSaleModal';
import { BottomDrawer } from '@/lib/BottomDrawer';
import { useAdaptiveWidth } from '@/hooks/useAdaptiveWidth';
import { TransferModal } from '../TransferModal';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectIsTransferMode,
  selectTransferList,
  setTranferList,
} from '@/store/app';
import { useRouter } from 'next/router';
import { useUpdateUserNFTs } from '@/hooks/useUpdateUserNFTs';

export const NFTCard = ({ data, availableTransfer }) => {
  const [open, setOpen] = useState(false);
  const [putOnSaleCard, setPutOnSaleCard] = useState(undefined);
  const [removeFromSaleCard, setRemoveFromSaleCard] = useState(undefined);
  const account = useWeb3ModalAccount();
  const { isTablet } = useAdaptiveWidth();
  const [transferActive, setTransferActive] = useState(false);
  const isTranfering = useSelector(selectIsTransferMode);
  const { push } = useRouter();
  const { getNFTs } = useUpdateUserNFTs();

  const {
    image,
    collection_name,
    name,
    video,
    price,
    creator_address,
    start_price,
    owner_address,
    trade_type,
    end_timestamp,
  } = data;

  const origin =
    typeof window !== 'undefined' && window.location.origin
      ? window.location.origin
      : '';
  const shareLink = `${origin}/collections/${data.collection_slug}/${data.slug}`;
  const isConnectedAddressEqualNFTOwner =
    owner_address?.toLowerCase() === account.address?.toLowerCase();

  const isBiddableAuction = trade_type === 'auction';
  const endTime = end_timestamp * 1000;
  const isAuctionLive = endTime > Date.now();
  const priceData = start_price || price;
  const dispatch = useDispatch();
  const transferList = useSelector(selectTransferList);
  const isInTransferList = transferList.find((item) => item.slug === data.slug);

  const handleSetCardOnSale = (data) => {
    if (data?.listing_id) {
      setRemoveFromSaleCard(data);
    } else {
      setPutOnSaleCard(data);
    }
  };

  const handleCardClick = (link) => {
    if (isTranfering && availableTransfer) {
      if (isInTransferList) {
        const filtered = transferList.filter((item) => item.slug !== data.slug);
        dispatch(setTranferList(filtered));
      } else {
        dispatch(setTranferList([...transferList, data]));
      }
      return;
    }

    push(link);
  };

  return (
    <>
      <div
        onClick={() => handleCardClick(shareLink)}
        className={cn(styles.card, {
          [styles.transfering]: isTranfering && availableTransfer,
          [styles.chosen]: isInTransferList && availableTransfer,
        })}
      >
        <div
          className={cn(styles.content, {
            [styles.transferingContent]: isTranfering && availableTransfer,
          })}
        >
          <Flex vertical gap={8}>
            <div className={styles.imageContainer}>
              {video ? (
                <ReactPlayer url={video} controls width="100%" height="100%" />
              ) : (
                <img
                  loading="lazy"
                  src={image}
                  className={styles.image}
                  alt="crypto"
                />
              )}
              <img
                src="/svg/ftn.svg"
                className={styles.collectionNFTIcon}
                alt=""
              />
              {isAuctionLive && !isConnectedAddressEqualNFTOwner && (
                <div className={styles.buyNow}>
                  <div className={styles.buyNowContent}>
                    <span className={styles.buyNowText}>Buy now</span>
                  </div>
                </div>
              )}
            </div>

            <Flex vertical={!isTablet} gap={5} className={styles.textDetails}>
              <Flex vertical gap={2}>
                <h3 className={cn(styles.name, styles.textDetailsRow)}>
                  {name}
                </h3>
                <div className={cn(styles.textDetailsRow, styles.textGray)}>
                  {collection_name}
                </div>
                <h4 className={styles.creator}>
                  By {truncate(creator_address, 4, 4, 11)}
                </h4>
              </Flex>

              {isTablet ? (
                <Button
                  icon={
                    isConnectedAddressEqualNFTOwner
                      ? '/svg/threeDots.svg'
                      : '/svg/shareIcon.svg'
                  }
                  type="button"
                  className={styles.shareButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(true);
                    e.preventDefault();
                  }}
                ></Button>
              ) : (
                <Popover
                  linkToCopy={shareLink}
                  buttonClassName={cn(styles.iconButton, {
                    [styles.iconHovered]: open,
                  })}
                  iconClassName={cn(styles.icon, styles.iconHovered)}
                  triggerClassName={cn(styles.iconContainer, {
                    [styles.iconFocused]: open,
                  })}
                  buttonIconType={
                    isConnectedAddressEqualNFTOwner ? 'threeDots' : 'share'
                  }
                  content={
                    isConnectedAddressEqualNFTOwner ? (
                      <NFTCardSettingsPopupContent
                        nftData={data}
                        setCardOnSale={() => handleSetCardOnSale(data)}
                        toggleTransfer={() =>
                          setTransferActive((prev) => !prev)
                        }
                        availableBulkTransfer={availableTransfer}
                        toggleDrawer={() => setOpen((prev) => !prev)}
                      />
                    ) : (
                      <SharePopupContent link={shareLink} />
                    )
                  }
                  onStateChange={(value) => setOpen(value)}
                />
              )}
            </Flex>

            <Flex justify="space-between" gap={8} className={styles.bottomInfo}>
              <VerticalDetails
                title="Price"
                value={priceData ? `${fromWei(priceData)} FTN` : '-'}
              />

              <VerticalDetails
                title={isBiddableAuction && isAuctionLive && 'Auction end'}
                value={
                  isBiddableAuction && isAuctionLive ? (
                    <Countdown timestamp={endTime} hourOnly />
                  ) : (
                    ''
                  )
                }
              />
            </Flex>
          </Flex>
        </div>
      </div>
      {isTablet && (
        <BottomDrawer
          active={open}
          closeFn={() => setOpen(false)}
          drawerId={data.slug}
          linkToCopy={shareLink}
        >
          {isConnectedAddressEqualNFTOwner ? (
            <NFTCardSettingsPopupContent
              nftData={data}
              setCardOnSale={() => handleSetCardOnSale(data)}
              toggleTransfer={() => setTransferActive((prev) => !prev)}
              toggleDrawer={() => setOpen((prev) => !prev)}
              availableBulkTransfer={availableTransfer}
            />
          ) : (
            <SharePopupContent link={shareLink} />
          )}
        </BottomDrawer>
      )}
      {putOnSaleCard && (
        <PutOnSaleModal
          cardData={putOnSaleCard}
          onClose={() => setPutOnSaleCard(undefined)}
        />
      )}
      {removeFromSaleCard && (
        <RemoveFromSaleModal
          cardData={removeFromSaleCard}
          onClose={() => setRemoveFromSaleCard(undefined)}
        />
      )}
      {transferActive && (
        <TransferModal
          onClose={() => {
            setTransferActive(false);
            getNFTs();
          }}
          nftData={data}
        />
      )}
    </>
  );
};

function VerticalDetails({ title, value }) {
  return (
    <Flex vertical gap={5}>
      <p className={cn(styles.verticalDetails, styles.textGray)}>{title}</p>
      <div className={styles.verticalDetailsValue}>{value}</div>
    </Flex>
  );
}
